<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import {
  isGuardedMediaUrl,
  useGuardedMedia,
} from 'dashboard/composables/useGuardedMedia';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
import FormattedContent from './Text/FormattedContent.vue';
import { detectMediaTones } from './mediaTone';
import { isGifMessage } from 'dashboard/helper/gifMediaHelper';
import {
  captionedVideoBoxRatio,
  classifyMediaAspectRatio,
  classifyGifAspectRatio,
  gifFrameStyle,
  MEDIA_ASPECT_CLASS,
  mediaFrameStyle,
  mediaAssetClass,
  captionReservedWidthStyle,
} from './mediaAspectLayout';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const videoRef = ref(null);
const duration = ref(0);
const detectedAspectRatio = ref('');
const isPlaying = ref(false);
const senderTone = ref('light');
const chevronTone = ref('light');
const timeTone = ref('light');
const {
  filteredCurrentChatAttachments,
  attachments,
  content,
  contentAttributes,
} = useMessageContext();

const captionExpanded = ref(false);
const caption = computed(() => {
  let value = String(content.value || '').trim();
  const normalizedValue = value.toLowerCase();
  const technicalMediaLabels = [
    'mensagem recebida',
    '[mensagem recebida]',
    'message received',
    '[message received]',
    'mídia recebida',
    '[mídia recebida]',
    'media received',
    '[media received]',
  ];

  if (technicalMediaLabels.includes(normalizedValue)) return '';

  // Aceleres: quando o rotulo tecnico vem colado como PREFIXO do texto real
  // (ex.: "[Mensagem recebida]\n\ntexto"), o match exato acima nao pega e
  // sobra o rotulo + linhas em branco antes da legenda, parecendo um vao
  // vazio na bolha. Corta so o prefixo tecnico nesse caso.
  const matchedLabel = technicalMediaLabels.find(label =>
    normalizedValue.startsWith(label)
  );
  if (matchedLabel) {
    value = value.slice(matchedLabel.length).trim();
  }

  return value;
});

const hasCaption = computed(() => Boolean(caption.value));
const hasMoreCaption = computed(() => caption.value.length > 768);
const visibleCaption = computed(() => {
  const limitedCaption = caption.value.slice(0, 1024);
  if (!captionExpanded.value && hasMoreCaption.value) {
    return `${limitedCaption.slice(0, 768)}...`;
  }

  return limitedCaption;
});

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const { guardedMediaSrc } = useGuardedMedia();
const videoSrc = computed(() => {
  const url = attachment.value?.dataUrl || '';
  if (!url) return '';
  return isGuardedMediaUrl(url) ? guardedMediaSrc(url) : url;
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

// Aceleres: mesma fonte de verdade da escolha da bolha (Message.vue), para
// que loop/mudo/etiqueta GIF nao divirjam do componente escolhido.
const isGif = computed(() =>
  isGifMessage({
    content_attributes: contentAttributes.value,
    attachments: attachments.value,
  })
);

const mediaAspectRatio = computed(() => {
  if (detectedAspectRatio.value) return detectedAspectRatio.value;

  const { width, height } = attachment.value || {};
  if (Number(width) > 0 && Number(height) > 0) {
    return `${width} / ${height}`;
  }
  return isReel.value ? '9 / 16' : '16 / 9';
});

const detectedNumericAspectRatio = ref(null);

const numericAspectRatio = computed(() => {
  if (detectedNumericAspectRatio.value) return detectedNumericAspectRatio.value;

  const { width, height } = attachment.value || {};
  return Number(width) > 0 && Number(height) > 0
    ? Number(width) / Number(height)
    : null;
});

// Aceleres: classificacao pelo aspect ratio real (nunca pela altura em
// pixels isolada), igual ao WhatsApp — compartilhada com Image.vue. O reel
// mantem o comportamento antigo (aspect-ratio natural, sem limites); o GIF
// usa a regua propria (classifyGifAspectRatio), que escala ate a caixa-alvo
// do formato em vez de respeitar o tamanho natural do arquivo.
const videoAspectClass = computed(() => {
  if (isGif.value) return classifyGifAspectRatio(numericAspectRatio.value);
  if (isReel.value) return null;
  return classifyMediaAspectRatio(numericAspectRatio.value);
});

// Reel e GIF ficam de fora: o reel tem tamanho proprio e o GIF ja escala ate
// a caixa-alvo do formato, entao nenhum dos dois sofre do encolhimento.
const isCaptionedVideo = computed(
  () => hasCaption.value && !isGif.value && !isReel.value
);

const playerStyle = computed(() => {
  if (isGif.value) {
    // Sem proporcao ainda (metadata do video a caminho): so o teto de
    // largura, para o frame nao nascer maior do que vai ficar.
    return (
      gifFrameStyle(numericAspectRatio.value) || {
        maxWidth: '100%',
        aspectRatio: mediaAspectRatio.value,
      }
    );
  }
  // Aceleres 2026-08-17: o video COM legenda entra aqui antes do retorno de
  // baixo. A bolha com legenda reserva 420px de largura sempre
  // (bubbleReservedStyle), e este ramo e o que faz o player preencher essa
  // largura. Quando a checagem ficava depois do `!videoAspectClass`, um video
  // que ainda nao reportou a proporcao — a ponte nao mandou as dimensoes e o
  // `loadedmetadata` nao chegou — saia sem o `width: 100%`: a bolha ficava
  // larga, o video estreito num canto e sobrava um vao verde do lado direito.
  //
  // Sem proporcao conhecida a caixa cai no padrao de `captionedVideoBoxRatio`,
  // e o `cover` do player recorta o excedente ate a medida real chegar.
  if (isCaptionedVideo.value) {
    return {
      width: '100%',
      '--acl-video-caption-ratio': String(
        captionedVideoBoxRatio(numericAspectRatio.value) ?? 0.875
      ),
    };
  }
  if (!videoAspectClass.value) return { aspectRatio: mediaAspectRatio.value };
  // Aceleres 2026-08-17: com legenda, quem manda na largura da bolha e a
  // LEGENDA — o video passa a preencher essa largura em vez de a ditar. Sem
  // isto, um video em pe era desenhado inteiro dentro do teto de altura e
  // saia com ~230px: a bolha encolhia ate ele e a legenda ficava presa numa
  // coluna estreita (comparacao com o WhatsApp em 17/08, video 474x850).
  // A altura sai da proporcao da CAIXA (captionedVideoBoxRatio), que segue a do
  // arquivo ate ao ponto em que a largura alvo ja encheria o teto de altura;
  // dai para baixo a caixa para de crescer e o `cover` (ver videoAssetClass)
  // recorta o excedente, em vez da tarja preta que o `contain` deixaria.
  //
  // A proporcao viaja como CUSTOM PROPERTY, e nao como `aspect-ratio` direto:
  // a regra do player em aceleres-overrides.css escreve `aspect-ratio: auto
  // !important`, que ganharia do estilo inline. E o mesmo desenho que a foto ja
  // usa (`--acl-photo-*`): o valor vem daqui, a regra la le a variavel.
  const frameStyle = mediaFrameStyle(videoAspectClass.value);

  if (videoAspectClass.value !== MEDIA_ASPECT_CLASS.VERY_VERTICAL) {
    return frameStyle;
  }

  return {
    ...frameStyle,
    '--acl-video-frame-width': frameStyle.width,
    '--acl-video-frame-height': frameStyle.height,
  };
});

// Aceleres 2026-08-17: mesma reserva de largura da foto (ver
// captionReservedWidthStyle). Aqui o sinal de "ja sei o tamanho" e
// `numericAspectRatio`: ele vem do `attachment` quando a ponte mandou as
// dimensoes e, senao, do `loadedmetadata` do proprio video — e ate esse
// momento a bolha com legenda nascia com a largura do texto espremido.
// Aceleres 2026-08-17: a reserva deixou de valer so enquanto o tamanho e
// desconhecido. Com legenda ela vale SEMPRE, e e o que impede o efeito
// contrario do ajuste acima: com o player em 100%, uma legenda curta faria a
// bolha encolher ate ao texto e o video encolheria junto. E a mesma largura
// alvo que a foto ja usa, entao midia com legenda tem uma medida so.
const bubbleReservedStyle = computed(() => {
  if (!hasCaption.value) return null;
  // Video com legenda: vale SEMPRE, e e o que impede o efeito contrario do
  // ajuste em playerStyle — com o player em 100%, uma legenda curta faria a
  // bolha encolher ate ao texto e o video encolheria junto.
  if (isCaptionedVideo.value) {
    return captionReservedWidthStyle();
  }

  // GIF e reel seguem como antes: so enquanto o tamanho e desconhecido.
  return numericAspectRatio.value ? null : captionReservedWidthStyle();
});

const videoAssetClass = computed(() => {
  // No GIF o frame ja tem exatamente a proporcao do arquivo, entao encher o
  // frame (w-full/h-full) com `contain` nao corta nem deforma nada — e o
  // `contain` garante isso mesmo no instante entre o frame ganhar tamanho e o
  // video reportar as dimensoes reais.
  if (isGif.value) return 'w-full h-full object-contain';
  if (!videoAspectClass.value) return 'w-full h-full object-cover';
  // Com legenda a caixa tem a largura da bolha e a altura vem cortada pelo
  // teto: `contain` deixaria tarja preta dos dois lados, `cover` preenche.
  if (isCaptionedVideo.value) return 'w-full h-full object-cover';
  return mediaAssetClass(videoAspectClass.value);
});

const mobileGifAssetClass =
  'max-md:[.acl-whatsapp-mobile-scope_.message-bubble-container_.acl-video-bubble.acl-video-bubble--gif:not(.acl-sticker-bubble)_&]:!max-h-[30dvh]';

const durationLabel = computed(() => {
  if (!Number.isFinite(duration.value) || duration.value <= 0) return '';
  const totalSeconds = Math.floor(duration.value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
});

const mediaDurationLabel = computed(() =>
  isGif.value ? '' : durationLabel.value
);

// Aceleres: GIF do WhatsApp chega como MP4 mudo. Na BOLHA ele roda sozinho 3
// vezes e para, mostrando o botao redondo escuro com "GIF" no centro; rolar
// para fora e voltar recomeca a contagem, e tocar nele abre a galeria, onde a
// animacao corre sem parar (pedido 2026-08-12).
//
// Sem som, sem controles, sem barra de progresso e sem duracao em nenhum dos
// dois. O que muda entre bolha e galeria e so quantas voltas: 3 aqui,
// infinitas la — por isso `video.loop` fica FALSE nesta bolha. Com `loop` o
// evento `ended` nunca dispara, e e nele que a contagem se apoia.
const GIF_MAX_LOOPS = 3;
const gifLoops = ref(0);
const gifFinished = ref(false);

const handleGifEnded = () => {
  const video = videoRef.value;
  gifLoops.value += 1;
  if (gifLoops.value >= GIF_MAX_LOOPS) {
    gifFinished.value = true;
    return;
  }
  if (video) {
    video.currentTime = 0;
    video.play().catch(() => {});
  }
};

const handleEnded = () => {
  isPlaying.value = false;
  if (isGif.value) handleGifEnded();
};

const replayGif = () => {
  const video = videoRef.value;
  if (!video) return;
  gifLoops.value = 0;
  gifFinished.value = false;
  video.currentTime = 0;
  video.play().catch(() => {});
};

// Aceleres: o GIF so roda quando entra na tela. Antes ele comecava no load,
// entao uma conversa com varios GIFs gastava as 3 voltas de todos enquanto
// ainda estavam fora da area visivel — ao rolar ate eles, ja estavam parados
// no botao "GIF". Sair da tela pausa; voltar recomeca a contagem.
let gifObserver = null;

const stopGifObserver = () => {
  if (!gifObserver) return;
  gifObserver.disconnect();
  gifObserver = null;
};

const startGifObserver = () => {
  stopGifObserver();
  const video = videoRef.value;
  if (!isGif.value || !video || typeof IntersectionObserver === 'undefined') {
    return;
  }

  gifObserver = new IntersectionObserver(
    entries => {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting) {
        replayGif();
        return;
      }
      videoRef.value?.pause?.();
    },
    { threshold: 0.35 }
  );
  gifObserver.observe(video);
};

onMounted(() => {
  // O <video> so existe depois do render; o watch cobre o caso do GIF ser
  // reconhecido depois (metadata/atributos chegando em seguida).
  nextTick(startGifObserver);
});
watch(isGif, startGifObserver);
onBeforeUnmount(stopGifObserver);

const handleLoadedMetadata = async event => {
  const video = event.currentTarget;
  duration.value = Number(video?.duration) || 0;

  if (Number(video?.videoWidth) > 0 && Number(video?.videoHeight) > 0) {
    detectedAspectRatio.value = `${video.videoWidth} / ${video.videoHeight}`;
    detectedNumericAspectRatio.value = video.videoWidth / video.videoHeight;
  }

  await nextTick();
  if (isGif.value) {
    video.muted = true;
    // Sem `loop`: a contagem das 3 repeticoes depende do evento `ended`, que o
    // proprio `loop` impediria de disparar. O loop infinito e da galeria.
    video.loop = false;
    gifLoops.value = 0;
    gifFinished.value = false;
    // Nao chama play() aqui: quem inicia e o IntersectionObserver, quando o
    // GIF aparece na tela.
    startGifObserver();
  }
};

const detectVideoTones = event => {
  try {
    const tones = detectMediaTones(event.currentTarget);
    senderTone.value = tones.sender;
    chevronTone.value = tones.chevron;
    timeTone.value = tones.time;
  } catch {
    senderTone.value = 'light';
    chevronTone.value = 'light';
    timeTone.value = 'light';
  }
};

const openVideoGallery = () => {
  showGallery.value = true;
};

const VIDEO_TAP_MOVE_TOLERANCE = 8;
let videoTouchPointer = null;

const onVideoPointerDown = event => {
  if (event.pointerType !== 'touch') return;

  videoTouchPointer = {
    id: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    moved: false,
  };
};

const onVideoPointerMove = event => {
  if (!videoTouchPointer || videoTouchPointer.id !== event.pointerId) return;

  const distance = Math.hypot(
    event.clientX - videoTouchPointer.startX,
    event.clientY - videoTouchPointer.startY
  );
  if (distance > VIDEO_TAP_MOVE_TOLERANCE) {
    videoTouchPointer.moved = true;
  }
};

const clearVideoTouchPointer = () => {
  videoTouchPointer = null;
};

const onVideoPointerUp = event => {
  if (!videoTouchPointer || videoTouchPointer.id !== event.pointerId) return;

  const shouldOpen = !videoTouchPointer.moved;
  clearVideoTouchPointer();
  if (!shouldOpen) return;

  // A pressão longa já abriu o menu da mensagem; soltar o vídeo não deve
  // abrir a galeria durante a mesma interação.
  const messageMenuIsOpen = event.currentTarget
    .closest('.message-bubble-container')
    ?.classList.contains('aceleres-context-open');
  if (messageMenuIsOpen) return;

  event.preventDefault();
  event.stopPropagation();
  openVideoGallery();
};

// Vídeo e GIF navegam pelo histórico da própria conversa. O provider resolve a
// lista pelo conversationId da mensagem, sem depender da seleção global.
const galleryAttachments = computed(() => filteredCurrentChatAttachments.value);
</script>

<template>
  <BaseBubble
    class="acl-video-bubble overflow-hidden p-1.5"
    :class="{
      'acl-video-bubble--playing': isPlaying,
      'acl-video-bubble--reel': isReel,
      'acl-video-bubble--gif': isGif,
      'acl-video-bubble--captioned': isCaptionedVideo,
    }"
    :style="bubbleReservedStyle"
    :data-bubble-name="isGif ? 'gif' : 'video'"
    :data-aspect-class="videoAspectClass || undefined"
    :data-sender-tone="senderTone"
    :data-chevron-tone="chevronTone"
    :data-time-tone="timeTone"
    :status-on-media="!hasCaption && !isGif && !hasError"
    media-caption
    show-sender-name
    role="button"
    tabindex="0"
    @click="showGallery = true"
    @keydown.enter.space.self.prevent="showGallery = true"
  >
    <!-- Aceleres 2026-08-12: `max-h-[420px]` e teto do VIDEO e o GIF fica de
         fora — a altura dele ja vem da caixa-alvo do formato (no maximo 420px
         de midia), e este teto conta tambem a moldura: 420 de midia mais 3px
         de cada lado dao 426, entao ele cortava 6px e, pelo aspect-ratio,
         encolhia a largura junto. Acontecia so no MP4 tratado como GIF, e por
         isso o mesmo ficheiro saia menor aqui do que num .gif de verdade.
         `acl-gif-media` e a mesma marca de area de midia que Image.vue usa,
         para as duas formas do GIF terem um gancho unico no CSS. -->
    <div
      class="acl-video-bubble__player relative overflow-hidden"
      :class="{
        'max-h-[420px]': !isGif,
        'acl-video-bubble__player--gif': isGif,
        'acl-gif-media': isGif,
      }"
      :style="playerStyle"
      @pointerdown="onVideoPointerDown"
      @pointermove="onVideoPointerMove"
      @pointerup="onVideoPointerUp"
      @pointercancel="clearVideoTouchPointer"
      @click.prevent.stop="openVideoGallery"
      @dblclick.stop="showGallery = true"
    >
      <div
        v-if="isReel"
        class="absolute right-0 z-[4] flex items-start justify-end p-2 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        ref="videoRef"
        :muted="isGif"
        :controls="false"
        :poster="attachment.thumbUrl || undefined"
        playsinline
        preload="metadata"
        class="acl-video-bubble__asset block"
        :class="[
          videoAssetClass,
          isGif ? mobileGifAssetClass : '',
          { 'skip-context-menu': !isGif },
        ]"
        :src="videoSrc || undefined"
        @click.prevent.stop="openVideoGallery"
        @error="videoSrc && handleError()"
        @loadeddata="detectVideoTones"
        @loadedmetadata="handleLoadedMetadata"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @ended="handleEnded"
      />

      <!-- Fim das 3 repeticoes: botao redondo escuro no centro, com "GIF" em
           branco. Tocar nele roda outras 3 vezes sem abrir a galeria. -->
      <button
        v-if="isGif && gifFinished"
        type="button"
        :aria-label="$t('CONVERSATION.REPLAY_GIF')"
        class="acl-video-bubble__gif-replay absolute inset-0 z-[4] flex items-center justify-center border-0 bg-transparent p-0"
        @pointerdown.prevent.stop="replayGif"
        @click.prevent.stop="replayGif"
      >
        <span class="flex items-center justify-center">GIF</span>
      </button>

      <button
        v-if="!isGif"
        type="button"
        class="acl-video-bubble__play absolute inset-0 z-[3] flex items-center justify-center border-0 bg-transparent p-0"
        @click.prevent.stop="openVideoGallery"
      >
        <!-- Aceleres: sem essas classes o botao/circulo ficava em fluxo
             normal (nao sobreposto ao video), empurrando a altura do frame
             pra baixo do video real e deixando um vao vazio antes do
             horario — corrigido posicionando como overlay absoluto. -->
        <span
          class="acl-video-bubble__play-circle flex size-12 items-center justify-center rounded-full bg-black/40 text-white"
        >
          <Icon icon="i-lucide-play" class="size-7" />
        </span>
      </button>

      <!-- Aceleres: no GIF o rodape nao tem camera nem duracao — leva a
           marca "GIPHY". Branco com sombra, igual ao horario: a marca fica
           por cima da propria animacao, onde cinza sumia. -->
      <span
        v-if="isGif"
        class="acl-video-bubble__giphy absolute left-2 bottom-2 z-[3] text-[12px] font-semibold tracking-wide"
      >
        GIPHY
      </span>

      <span
        v-if="mediaDurationLabel"
        class="acl-video-bubble__duration absolute left-2 bottom-2 z-[3] flex items-center gap-1 rounded-full bg-black/45 px-1.5 py-0.5 text-[12px] font-medium text-white"
      >
        <svg
          viewBox="0 0 16 14"
          height="14"
          width="16"
          preserveAspectRatio="xMidYMid meet"
          version="1.1"
          x="0px"
          y="0px"
          enable-background="new 0 0 16 14"
          aria-hidden="true"
        >
          <title>msg-video</title>
          <path
            fill="currentColor"
            d="M14.99 2.67 11.5 5.76V3.49a1.2 1.2 0 0 0-1.2-1.19H1.7c-.67 0-1.2.53-1.2 1.19v7.14c0 .65.53 1.19 1.19 1.19h8.63a1.2 1.2 0 0 0 1.19-1.2v-2.3l3.48 3.1V2.67z"
          />
        </svg>
        {{ mediaDurationLabel }}
      </span>
    </div>
    <div
      v-if="hasCaption"
      class="acl-video-bubble__caption acl-media-caption-surface box-border w-full min-h-9 pl-2 pb-6 pt-2 text-[15.8px] leading-5"
      @click.stop
    >
      <span class="inline">
        <FormattedContent
          class="acl-message-caption [&_p]:!inline [&_p]:!m-0"
          :content="visibleCaption"
        />
        <button
          v-if="hasMoreCaption && !captionExpanded"
          type="button"
          class="inline font-bold text-[#1b8755] no-underline -ml-px !m-0 !p-0 border-0 leading-none"
          @click.stop="captionExpanded = true"
        >
          Ler mais
        </button>
      </span>
    </div>
    <slot name="menu-trigger" :chevron-tone="chevronTone" />
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="galleryAttachments"
    :initial-caption="caption"
    :initial-is-gif="isGif"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>

<style>
/* Aceleres 2026-08-18: as medidas do vídeo COM LEGENDA saíram daqui para
   `public/aceleres-overrides.css`, junto das do vídeo sem legenda.

   Elas nunca chegaram a valer neste ficheiro: o bloco não é `scoped`, então os
   seletores contavam (0,4,0), e as regras do vídeo sem legenda no overrides
   contam (0,5,0) — ganham por especificidade, não por ordem. O efeito medido
   era um player de 301x344 numa bolha de 420, com o vídeo transbordando a
   caixa e verde a sobrar à direita. Ter as duas medidas no mesmo ficheiro
   também evita que o par volte a divergir. */
</style>
